import React, { useState, useEffect, useRef } from 'react';
import Header from '../../components/common/Header-w';
import Button from '../../components/ui/Button2';
import EditText from '../../components/ui/EditText';
import CountUp from '../../components/ui/CountUp';
import "../../Website.css";
import { useLocation } from 'react-router-dom';
import CareerFooter from '../AboutUs/CareerFooter';
import { school_inquiry } from '../../../Services/Website/WebsiteApi';

// Define WhyChooseSeekJobsSection as a separate functional component
const WhyChooseSeekJobsSection = ({ features }) => {
  // Data preparation for the original desktop layout. This logic is preserved exactly as it was.
  const topRowFeatures = features.slice(0, 7);
  const bottomSectionFeatures = features.slice(7);

  const formsFeature = bottomSectionFeatures.find((f) => f.title === 'Forms');
  const dashboardFeature = bottomSectionFeatures.find(
    (f) => f.title === 'Real-Time Hiring Dashboard'
  );

  const remainingBottomFeatures = bottomSectionFeatures.filter(
    (f) => f.title !== 'Forms' && f.title !== 'Real-Time Hiring Dashboard'
  );

  return (
    // Removed mt-[-350px] from the section tag to prevent potential layout issues on mobile.
    // If specific vertical positioning is required, consider using padding/margin on the parent or adjacent sections.
    <section style={{ paddingBottom: 0 }} className="tw-relative tw-mt-[-350px] tw-py-12 sm:tw-py-16 lg:tw-py-20 tw-px-4 sm:tw-px-6 lg:tw-px-14 tw-overflow-hidden">
      <div className="tw-max-w-7xl tw-mx-auto tw-relative tw-z-0 tw-h-full">
        {/* Decorative Background Blob */}
        <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-pointer-events-none">
          <img
            src="/images/img_frame_2018777023.png"
            alt="Decorative background shape"
            className="tw-w-full tw-h-full tw-object-contain tw-mx-auto tw-z-10 tw-absolute tw-left-1/2 tw-top-1/2 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-transform tw-scale-125
                   lg:tw-w-[200%] lg:tw-max-w-none lg:tw-h-[130%] lg:tw-object-fill lg:tw-object-center lg:tw-z-40 lg:tw-scale-100 lg:-tw-translate-y-1/2"
          />
        </div>

        {/* --- MOBILE VIEW --- */}
        <div className="lg:tw-hidden tw-relative tw-z-30">
          {/* Top Blank Tiles (Mobile only) */}
          <div className="tw-grid tw-grid-cols-2 tw-gap-4 tw-mb-8 sm:tw-hidden">
            {Array.from({ length: 2 }).map((_, index) => (
              <div
                key={`mobile-blank-top-2-${index}`}
                className={`tw-bg-global-10 tw-rounded-3xl tw-p-4 tw-shadow-[0px_0px_24px_#00000019] tw-border tw-border-[#d9d9d9] tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-aspect-square tw-min-h-[150px] tw-opacity-50 ${index % 2 === 0 ? 'tw-animate-heartbeat-1' : 'tw-animate-heartbeat-2'}`}
              />
            ))}
          </div>

          {/* Section Heading */}
          <h2 className="tw-text-3xl sm:tw-text-4xl tw-font-semibold tw-text-global-5 tw-text-center tw-mt-48 tw-mb-8 sm:tw-mb-12 tw-relative tw-z-30">
            Why choose SeekJobs?
          </h2>

          {/* Feature Tiles */}
          <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 md:tw-grid-cols-4 tw-gap-4 sm:tw-gap-6 tw-mb-8">
            {features.map((feature, index) => (
              <div
                key={feature.title || `mobile-feature-${index}`}
                className={`tw-bg-global-10 tw-rounded-3xl tw-p-4 sm:tw-p-6 tw-shadow-[0px_0px_24px_#00000019] tw-border tw-border-[#d9d9d9] tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-aspect-square tw-min-h-[150px] sm:tw-min-h-[180px] ${feature.isBehindBlob ? 'tw-opacity-50 tw-grayscale' : ''} ${index % 2 === 0 ? 'tw-animate-heartbeat-1' : 'tw-animate-heartbeat-2'}`}
              >
                <img src={feature.icon} alt={feature.title} className="tw-w-12 tw-h-12 sm:tw-w-16 sm:tw-h-16 tw-mx-auto tw-mb-2 sm:tw-mb-4" />
                <h3 className="tw-text-xs sm:tw-text-sm tw-font-semibold tw-text-global-4 tw-leading-tight">{feature.title}</h3>
              </div>
            ))}
          </div>

          {/* Bottom Blank Tiles (Mobile only) */}
          <div className="tw-grid tw-grid-cols-2 tw-gap-4 sm:tw-hidden">
            {Array.from({ length: 2 }).map((_, index) => (
              <div
                key={`mobile-blank-bottom-2-${index}`}
                className={`tw-bg-global-10 tw-rounded-3xl tw-p-4 tw-shadow-[0px_0px_24px_#00000019] tw-border tw-border-[#d9d9d9] tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-aspect-square tw-min-h-[150px] tw-opacity-50 ${index % 2 === 0 ? 'tw-animate-heartbeat-1' : 'tw-animate-heartbeat-2'}`}
              />
            ))}
          </div>
        </div>

        {/* --- DESKTOP VIEW --- */}
        <div className="tw-hidden lg:tw-block">
          {/* Top Blank Row */}
          <div className="tw-flex tw-justify-center tw-gap-10 tw-mb-8">
            {Array.from({ length: 7 }).map((_, index) => (
              <div
                key={`desktop-blank-top-${index}`}
                className={`tw-bg-global-10 tw-rounded-3xl tw-p-5 tw-shadow-[0px_0px_24px_#00000019] tw-border tw-border-[#d9d9d9] tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-aspect-square tw-w-[200px] tw-h-[200px] tw-opacity-50 ${index % 2 === 0 ? 'tw-animate-heartbeat-1' : 'tw-animate-heartbeat-2'}`}
              />
            ))}
          </div>

          {/* Feature Row */}
          <div className="tw-flex tw-justify-center tw-gap-10 tw-mb-8">
            {topRowFeatures.map((feature, index) => (
              <div
                key={`desktop-top-feature-${index}`}
                className={`tw-bg-global-10 tw-rounded-3xl tw-p-5 tw-shadow-[0px_0px_24px_#00000019] tw-border tw-border-[#d9d9d9] tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-aspect-square tw-w-[200px] tw-h-[200px] ${index % 2 === 0 ? 'tw-animate-heartbeat-1' : 'tw-animate-heartbeat-2'}`}
              >
                <img src={feature.icon} alt={feature.title} className="tw-w-16 tw-h-16 sm:tw-w-20 sm:tw-h-20 tw-mb-6" />
                <h3 className="tw-text-sm lg:tw-text-base tw-font-semibold tw-text-global-4 tw-leading-tight">{feature.title}</h3>
              </div>
            ))}
          </div>

          {/* Title Center Row */}
          <div className="tw-flex tw-items-center tw-justify-center tw-gap-10 tw-my-16">
            <div className="tw-bg-global-10 tw-rounded-3xl tw-p-5 tw-shadow-[0px_0px_24px_#00000019] tw-border tw-border-[#d9d9d9] tw-aspect-square tw-w-[200px] tw-h-[200px] tw-opacity-50 tw-animate-heartbeat-1" />

            {formsFeature && (
              <div className="tw-bg-global-10 tw-rounded-3xl tw-p-5 tw-flex tw-flex-col tw-items-center tw-justify-center tw-shadow-[0px_0px_24px_#00000019] tw-border tw-border-[#d9d9d9] tw-aspect-square tw-w-[200px] tw-h-[200px] tw-animate-heartbeat-2">
                <img src={formsFeature.icon} alt={formsFeature.title} className="tw-w-16 tw-h-16 sm:tw-w-20 sm:tw-h-20 tw-mb-6" />
                <h3 className="tw-text-sm lg:tw-text-base tw-font-semibold tw-text-global-4 tw-leading-tight">{formsFeature.title}</h3>
              </div>
            )}

            <h2 className="tw-text-6xl tw-font-semibold tw-text-global-5 tw-text-center tw-order-first lg:tw-order-none tw-text-nowrap">
              Why choose SeekJobs?
            </h2>

            {dashboardFeature && (
              <div className="tw-bg-global-10 tw-rounded-3xl tw-p-5 tw-shadow-[0px_0px_24px_#00000019] tw-flex tw-flex-col tw-items-center tw-justify-center tw-border tw-border-[#d9d9d9] tw-aspect-square tw-w-[200px] tw-h-[200px] tw-animate-heartbeat-1">
                <img src={dashboardFeature.icon} alt={dashboardFeature.title} className="tw-w-16 tw-h-16 sm:tw-w-20 sm:tw-h-20 tw-mb-6" />
                <h3 className="tw-text-sm tw-text-center lg:tw-text-base tw-font-semibold tw-text-global-4 tw-leading-tight">{dashboardFeature.title}</h3>
              </div>
            )}

            <div className="tw-bg-global-10 tw-rounded-3xl tw-p-5 tw-shadow-[0px_0px_24px_#00000019] tw-border tw-border-[#d9d9d9] tw-aspect-square tw-w-[200px] tw-h-[200px] tw-opacity-50 tw-animate-heartbeat-2" />
          </div>

          {/* Bottom Feature Row */}
          <div className="tw-flex tw-justify-center tw-gap-10 tw-mb-8">
            {remainingBottomFeatures.map((feature, index) => (
              <div
                key={`desktop-bottom-feature-${index}`}
                className={`tw-bg-global-10 tw-rounded-3xl tw-p-5 tw-shadow-[0px_0px_24px_#00000019] tw-border tw-border-[#d9d9d9] tw-aspect-square tw-w-[200px] tw-h-[200px] tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center ${feature.isBehindBlob ? 'tw-opacity-50 tw-grayscale tw-translate-y-8' : ''} ${index % 2 === 0 ? 'tw-animate-heartbeat-1' : 'tw-animate-heartbeat-2'}`}
              >
                <img src={feature.icon} alt={feature.title} className="tw-w-16 tw-h-16 sm:tw-w-20 sm:tw-h-20 tw-mb-6" />
                <h3 className="tw-text-sm lg:tw-text-base tw-font-semibold tw-text-global-4 tw-leading-tight">{feature.title}</h3>
              </div>
            ))}
          </div>

          {/* Bottom Blank Row */}
          <div className="tw-flex tw-justify-center tw-gap-10 tw-mb-8">
            {Array.from({ length: 7 }).map((_, index) => (
              <div
                key={`desktop-blank-bottom-${index}`}
                className={`tw-bg-global-10 tw-rounded-3xl tw-p-5 tw-shadow-[0px_0px_24px_#00000019] tw-border tw-border-[#d9d9d9] tw-aspect-square tw-w-[200px] tw-h-[200px] tw-opacity-50 ${index % 2 === 0 ? 'tw-animate-heartbeat-1' : 'tw-animate-heartbeat-2'}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>

  );
};

const CareerCentersPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    institutionName: '',
    phoneNumber: '',
    workEmail: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [selectedPopulation, setSelectedPopulation] = useState('');
  const [selectedPriorities, setSelectedPriorities] = useState('');
  const resultsSectionRef = useRef(null);
  const [resultsVisible, setResultsVisible] = useState(false);
  const location = useLocation();
  const [statusMessage, setStatusMessage] = useState({
    show: false,
    type: '', // 'success' or 'error'
    text: '',
  });


  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const yOffset = -50; // Adjust to your header height
          const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
          el.scrollIntoView({ top: y, behavior: 'smooth' });
        }
      }, 100); // Delay ensures element is rendered
    }
  }, [location]);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setResultsVisible(true);
          observer.unobserve(entry.target); // Animate only once
        }
      },
      {
        threshold: 0.1, // Trigger when 10% of the section is visible
      }
    );

    const currentRef = resultsSectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => currentRef && observer.unobserve(currentRef);
  }, []);



  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 10); // only digits, max 10

    const part1 = digits.slice(0, 3);
    const part2 = digits.slice(3, 6);
    const part3 = digits.slice(6, 10);

    if (digits.length === 0) return "";
    if (digits.length <= 3) return `(${part1}`;
    if (digits.length <= 6) return `(${part1}) ${part2}`;
    return `(${part1}) ${part2}-${part3}`;
  };


  const handleInputChange = (field) => (e) => {
    let input = e.target.value;

    // Only process phone number field
    if (field === "phoneNumber") {
      const formatted = formatPhoneNumber(input);
      setFormData((prev) => ({ ...prev, [field]: formatted }));
    } else if (field === "workEmail") {
      setFormData((prev) => ({ ...prev, [field]: input }));
    } else {
      const filteredValue = input.replace(/[^a-zA-Z\s]/g, "");
      setFormData((prev) => ({ ...prev, [field]: filteredValue }));
    }
  };
  const handleKeyDown = (e) => {
    const allowed = ["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete"];
    if (!/[0-9]/.test(e.key) && !allowed.includes(e.key)) {
      e.preventDefault(); // Block non-digit keys
    }
  };


  const handlePopulationChange = (value) => {
    setSelectedPopulation(value);
  };

  const handlePriorityChange = (value) => {
    setSelectedPriorities(value);
  };
  const [expandedIndex, setExpandedIndex] = useState(null); // First open

  const toggleFAQ = (index) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };
  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) newErrors.fullName = 'Full Name is required';
    if (!formData.institutionName.trim()) newErrors.institutionName = 'Institution Name is required';
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone Number is required';
    if (!formData.workEmail.trim()) {
      newErrors.workEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.workEmail)) {
      newErrors.workEmail = 'Invalid email format';
    }
    if (!formData.message) newErrors.message = 'Message is required'
    if (!selectedPopulation) newErrors.student_population = 'Please select student population';
    if (!selectedPriorities) newErrors.priority = 'Select at least one priority';

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleRequestCall = async () => {
    if (!validateForm()) {
      setStatusMessage({
        show: true,
        type: 'Error',
        text: "Please fill out all required fields.",
      });
      setIsSubmitting(false);
      return
    };

    const requestData = {
      full_name: formData.fullName,
      institution_name: formData.institutionName,
      work_email: formData.workEmail,
      phone: formData.phoneNumber,
      student_population: selectedPopulation,
      priority: selectedPriorities,
      message: formData.message,
    };

    try {
      setIsSubmitting(true);
      setStatusMessage({ show: false, type: '', text: '' });

      const response = await school_inquiry(requestData);
      if (response?.code === "ERR_BAD_REQUEST") {
        throw new Error(response.response.data.error || 'Request failed');
      }

      setStatusMessage({
        show: true,
        type: 'success',
        text: response.message || 'Inquiry submitted successfully!',
      });

      setFormData({
        fullName: '',
        institutionName: '',
        phoneNumber: '',
        workEmail: '',
        message: '',
      });
      setSelectedPopulation('');
      setSelectedPriorities([]);
      setErrors({});
    } catch (error) {
      console.error('Error:', error);
      setStatusMessage({
        show: true,
        type: 'error',
        text:
          error?.response?.data?.error ||
          error?.message ||
          'Something went wrong. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };




  const problemStats = [
    {
      percentage: '42%',
      description: 'of graduates find jobs within 6 months',
      bgColor: 'tw-bg-[linear-gradient(180deg,#e5e9ff_0%,_#ced1e5_100%)]',
    },
    {
      percentage: '68%',
      description: 'of career centers lack placement tracking',
      bgColor: 'tw-bg-[linear-gradient(180deg,#e5e9ff_0%,_#ced1e5_100%)]',
    },
    {
      percentage: '73%',
      description: 'of employers say grads are not job-ready',
      bgColor: 'tw-bg-[linear-gradient(180deg,#e5e9ff_0%,_#ced1e5_100%)]',
    },
  ];

  const resultStats = [
    {
      percentage: '65%',
      description: 'higher student engagement',
      bgColor: 'tw-bg-global-2',
      icon: '/images/img_growth.png',
    },
    {
      percentage: '40%',
      description: 'faster job placement',
      bgColor: 'tw-bg-global-4',
      icon: '/images/img_vector.svg',
    },
    {
      percentage: '3x',
      description: 'better alumni satisfaction',
      bgColor: 'tw-bg-global-9',
      icon: '/images/img_satisfaction.png',
    },
    {
      percentage: '85%',
      description: 'improved student confidence',
      bgColor: 'tw-bg-global-3',
      icon: '/images/img_star.png',
    },
  ];

  const solutionFeatures = [
    {
      title: 'Monitor job activity, applications, and placements instantly.',
      image: '/images/img_image_26.png',
    },
    {
      title: 'Real-Time Student Tracking',
      image: '/images/image 27.png',
    },
    {
      title: 'Resume rates, interview success, job offers, salary outcomes.',
      image: '/images/LoginActivity.png',
    },
    {
      title: '5M+ Job Database Access',
      image: '/images/Jobs.png',
    },
    {
      title: 'Premium opportunities include exclusive career center partnerships.',
      image: '/images/PremiumOpportunities.png',
    },

    {
      title: 'Success Metrics Dashboard',
      image: '/images/SuccessMetrics.png',
    },
  ];

  const whyChooseFeatures = [
    // Top row features (7 items) - appear in front of the blob
    { icon: '/images/img_reports.svg', title: 'Custom Reports', isBehindBlob: false },
    {
      icon: '/images/img_graduation_hat.svg',
      title: 'Built for higher education',
      isBehindBlob: false,
    },
    {
      icon: '/images/img_university.svg',
      title: 'University-Focused Platform',
      isBehindBlob: false,
    },
    { icon: '/images/img_admin.svg', title: 'Personal support', isBehindBlob: false },
    { icon: '/images/img_account.svg', title: 'Dedicated Account Manager', isBehindBlob: false },
    { icon: '/images/img_integration.svg', title: 'Seamless Integration', isBehindBlob: false },
    {
      icon: '/images/img_multi_mac_os_window.svg',
      title: 'Works with existing systems',
      isBehindBlob: false,
    },

    // Bottom section features (9 items) - order based on visual appearance in the image
    // 'Forms' and 'Real-Time Hiring Dashboard' are marked as 'isBehindBlob: true' for special styling
    { icon: '/images/img_form.svg', title: 'Forms', isBehindBlob: true },
    { icon: '/images/img_360_view.svg', title: '360 View Dashboard', isBehindBlob: false },
    { icon: '/images/img_search.svg', title: 'Connected Search', isBehindBlob: false },
    { icon: '/images/img_manager.svg', title: 'Manage Students', isBehindBlob: false },
    { icon: '/images/img_ai_portrait.svg', title: 'AI Resume Optimization', isBehindBlob: false },
    { icon: '/images/img_skills.svg', title: 'Skills Assessment', isBehindBlob: false },
    { icon: '/images/img_network.svg', title: 'Employer Network', isBehindBlob: false },
    { icon: '/images/img_hiring_employee.svg', title: 'Alumni Network', isBehindBlob: false },
    {
      icon: '/images/img_realtime_dashboard.png',
      title: 'Real-Time Hiring Dashboard',
      isBehindBlob: true,
    },
  ];
  const faqs = [
    {
      question: '1. Is SeekJobs for universities and career centers?',
      answer:
        'Absolutely! We provide admin dashboards, student analytics, placement tracking, and premium features to boost your career services impact.',
    },
    {
      question: '2. Is SeekJobs just for students?',
      answer:
        'Nope—we serve all job seekers, whether you`re in school, recently graduated, or switching careers.',
    },
    {
      question: '3. How accurate are job matches for students?',
      answer:
        'Our AI uses skill-based matching tailored to early-career profiles—students receive curated roles that match their background and education.',
    },
  ];

  return (
    <div className="tw-w-full tw-relative tw-overflow-hidden scrollbar-hide theme-career-partners tw-w-full tw-bg-global-7 tw-tracking-tight-4">
      <main className="tw-relative tw-z-10">
        {' '}
        <Header />
        {/* Hero Section */}
        <section
          className="tw-relative tw-w-full tw-h-[400px] sm:tw-h-[500px] md:tw-h-[600px] lg:tw-h-[728px] tw-bg-cover tw-bg-center tw-bg-no-repeat"
          style={{ backgroundImage: "url('/images/img_shutterstock2404926321_1.jpeg')" }}
        >
          <div className="tw-absolute tw-inset-0 tw-bg-black tw-bg-opacity-40"></div>
          <div className="tw-relative tw-z-10 tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-px-4 sm:tw-px-6 lg:tw-px-14 tw-text-center">
            <div className="tw-max-w-4xl tw-mx-auto">
              <h1 className="tw-text-2xl sm:tw-text-4xl md:tw-text-6xl lg:tw-text-7xl tw-font-semibold tw-leading-tight tw-mb-4 sm:tw-mb-6 lg:tw-mb-8">
                <span className="tw-text-global-7 tw-block tw-mb-2">Your Students' Success</span>
                <span className="tw-text-global-8 tw-block">Your Institution's Legacy</span>
              </h1>
              <p className="tw-text-lg sm:tw-text-xl md:tw-text-2xl lg:tw-text-4xl tw-text-global-8 tw-max-w-2xl tw-mx-auto">
                Real-time tracking, analytics, and
              </p>
              <p className="tw-text-lg sm:tw-text-xl md:tw-text-2xl lg:tw-text-4xl tw-text-global-8 tw-mb-6 sm:tw-mb-8 lg:tw-mb-12 tw-max-w-2xl tw-mx-auto">
                5M+ job opportunities in one platform
              </p>
              <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 tw-justify-center tw-items-center">
                <Button
                  onClick={() => {
                    const element = document.getElementById('ContactUs');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' }); // remove `behavior` if you don't want smooth scroll
                    }
                  }}
                  variant="primary"
                  size="large"
                  className="tw-border-none tw-w-full sm:tw-w-auto"
                >
                  Schedule Demo
                </Button>
                <Button
                  onClick={() => {
                    const element = document.getElementById('ContactUs');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' }); // remove `behavior` if you don't want smooth scroll
                    }
                  }}
                  variant="secondary"
                  size="large"
                  className="tw-w-full sm:tw-w-auto tw-bg-global-10 tw-text-global-1 tw-border-none"
                >
                  Contact Us
                </Button>
              </div>
            </div>
          </div>
        </section>
        {/* Problem Section */}
        <section className="tw-py-12 sm:tw-py-16 lg:tw-py-20 tw-px-4 sm:tw-px-6 lg:tw-px-14">
          <div className="tw-max-w-7xl tw-mx-auto">
            <h2 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-6xl tw-font-semibold tw-text-global-2 tw-text-center tw-mb-8 sm:tw-mb-12 lg:tw-mb-16">
              The Problem
            </h2>
            <div className="tw-flex tw-flex-col tw-gap-6 lg:tw-grid lg:tw-grid-cols-3 lg:tw-gap-10 tw-mb-8 sm:tw-mb-12">
              {problemStats.map((stat, index) => (
                <div key={index} className={`${stat.bgColor} tw-rounded-3xl tw-p-6 sm:tw-p-8 lg:tw-p-10 tw-shadow-[0px_4px_4px_#888888ff]`}>
                  <div className="tw-text-4xl sm:tw-text-6xl lg:tw-text-8xl tw-font-semibold tw-text-global-6 tw-mb-4">
                    {stat.percentage}
                  </div>
                  <p className="tw-text-base sm:tw-text-lg lg:tw-text-2xl tw-font-semibold tw-leading-tight tw-bg-gradient-to-r tw-from-[#7E8299] tw-to-[#2A2B33] tw-bg-clip-text tw-text-transparent">
                    {stat.description}
                  </p>
                </div>
              ))}
            </div>
            <p className="tw-text-xl sm:tw-text-2xl lg:tw-text-4xl tw-font-semibold tw-text-global-2 tw-text-center">
              Your students need better. You need better tools.
            </p>
          </div>
        </section>
        {/* Solution Section & Results Section */}
        <section
          className="tw-relative tw-z-10 tw-py-12 sm:tw-py-16 lg:tw-py-20 tw-px-4 sm:tw-px-6 lg:tw-px-14 tw-bg-cover tw-bg-top"
          style={{ backgroundImage: "url('/images/Vector 3.png')" }}
        >
          <div className="tw-max-w-7xl tw-mx-auto tw-text-center">
            <h2 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-6xl tw-font-semibold tw-text-global-1 tw-mb-4 sm:tw-mb-6">
              The SeekJobs Solution
            </h2>
            <Button
              onClick={() => {
                const element = document.getElementById('ContactUs');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' }); // remove `behavior` if you don't want smooth scroll
                }
              }}
              variant="primary"
              size="large"
              className="tw-mb-8 sm:tw-mb-12 lg:tw-mb-16 tw-border-none"
            >
              Contact Us
            </Button>

            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-6 lg:tw-gap-10">
              {solutionFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="tw-relative tw-bg-global-5 tw-rounded-xl tw-shadow-[0px_4px_16px_#0000003f] tw-border-[6px] tw-border-global-10 tw-overflow-hidden"
                >
                  {feature.image && (
                    <img src={feature.image} alt={feature.title} className="tw-w-full tw-h-70 tw-object-cover tw-object-center" />
                  )}
                  <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-2/5 tw-bg-gradient-to-t tw-from-white/30 tw-to-transparent tw-pointer-events-none"></div>
                  <h3 className="tw-absolute tw-bottom-0 tw-left-0 tw-p-4 sm:tw-p-6 tw-text-lg sm:tw-text-2xl lg:tw-text-2xl tw-font-semibold tw-text-white tw-text-global-4 tw-text-left">
                    {feature.title}
                  </h3>
                </div>
              ))}
            </div>
          </div>

          <section ref={resultsSectionRef} className="tw-py-24 sm:tw-py-32 lg:tw-py-40 tw-px-4 sm:tw-px-6 lg:tw-px-14">
            <div className="tw-max-w-7xl tw-mx-auto">
              <h2 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-6xl tw-font-semibold tw-text-global-2 tw-text-center tw-mb-8 sm:tw-mb-12 lg:tw-mb-16">
                Results That Matter
              </h2>
              <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-6 lg:tw-gap-10">
                {resultStats.map((stat, index) => (
                  <div
                    key={index}
                    className={`${stat.bgColor} tw-rounded-3xl tw-p-6 sm:tw-p-8 tw-relative tw-shadow-[0px_4px_4px_#888888ff] tw-flex tw-flex-col tw-min-h-[220px]`}
                  >
                    <div className="tw-relative tw-z-10 tw-flex tw-flex-col tw-flex-grow">
                      <div className="tw-text-3xl sm:tw-text-5xl lg:tw-text-7xl tw-font-semibold tw-bg-[linear-gradient(180deg,#086ee5_0%,_#043d7f_100%)] tw-bg-clip-text tw-text-transparent tw-mb-4 tw-tabular-nums">
                        {resultsVisible && <CountUp value={stat.percentage} duration={2000} />}
                      </div>
                      <p className="tw-text-base sm:tw-text-lg lg:tw-text-2xl tw-font-semibold tw-text-global-4 tw-leading-tight tw-mt-auto">
                        {stat.description}
                      </p>
                    </div>
                    {stat.icon && (
                      <img
                        src={stat.icon}
                        alt="Stat icon"
                        className="tw-absolute tw-bottom-4 tw-right-4 tw-w-20 tw-h-20 sm:tw-w-24 sm:tw-h-24 lg:tw-w-32 lg:tw-h-32 tw-opacity-80"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </section>
        {/* Why Choose Section - Now rendered as a component */}
        <WhyChooseSeekJobsSection features={whyChooseFeatures} />
        {/* Contact Form Section */}
        <div
          style={{ marginTop: '-50px' }}
          className="tw-font-inter tw-w-full tw-relative tw-overflow-hidden scrollbar-hide tw-bg-global-8"
        >
          <section
            className="md:tw-py-12 lg:tw-py-8"
            style={{
              backgroundImage: `radial-gradient(rgba(126, 130, 153, 0.6) 1px, transparent 1px)`,
              backgroundSize: `30px 30px`,
              backgroundColor: '#ffffffff',
            }}
          >
            <div className="tw-px-4 md:tw-px-8 lg:tw-px-12 xl:tw-px-32">
              <div className="tw-relative tw-pt-8">
                <h2 className="tw-font-inter tw-font-semibold tw-text-4xl sm:tw-text-5xl md:tw-text-6xl lg:tw-text-7xl xl:tw-text-7xl 2xl:tw-text-7xl 3xl:tw-text-8xl tw-leading-tight md:tw-leading-[121px] tw-text-global-1 tw-text-center tw-mb-8 md:tw-mb-16 tw-tracking-tight-4">
                  Frequently Asked Questions
                </h2>

                <div className="tw-space-y-3">
                  {faqs.map((faq, index) => (
                    <div key={index}>
                      <div
                        onClick={() => toggleFAQ(index)}
                        className={`tw-rounded-[12px] tw-cursor-pointer tw-transition-all ${
                          expandedIndex === index ? 'tw-bg-global-12' : ''
                        }`}
                        style={{ padding: '10px' }}
                      >
                        <div className="tw-flex tw-justify-between tw-items-center">
                          <h3
                            className={`tw-font-inter tw-font-semibold tw-text-[16px] sm:tw-text-[18px] lg:tw-text-[19px] tw-leading-[24px] sm:tw-leading-[26px] ${
                              expandedIndex === index ? 'tw-text-global-8' : 'tw-global-text-1'
                            }`}
                          >
                            {faq.question}
                          </h3>
                          <img
                            src={expandedIndex === index ? '/images/img_caret.svg' : '/images/img_caret_black.svg'}
                            alt="Toggle"
                            className={`tw-transition-transform ${
                              expandedIndex === index ? 'sm:tw-w-6 tw-h-5 sm:tw-h-6' : 'sm:tw-w-[1rem] tw-h-[1rem] sm:tw-h-[1.5rem]'
                            }`}
                          />
                        </div>

                        {expandedIndex === index && (
                          <p
                            className={`tw-font-inter tw-font-medium tw-text-[14px] sm:tw-text-[16px] lg:tw-text-[19px] tw-leading-[22px] sm:tw-leading-[26px] ${
                              expandedIndex === index ? 'tw-text-global-8' : 'tw-text-global-6'
                            } tw-mt-3 tw-pl-3 sm:tw-pl-6 lg:tw-pl-10`}
                          >
                            {faq.answer}
                          </p>
                        )}
                      </div>

                      {index < faqs.length - 1 && <div className="tw-bg-global-7 tw-h-px tw-w-full tw-my-4"></div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>
        <section
          style={{ marginTop: '-45px' }}
          id="ContactUs"
          className="tw-relative tw-z-20 tw-py-12 sm:tw-py-16 lg:tw-py-16 tw-px-4 sm:tw-px-6 lg:tw-px-14"
        >
          <div className="tw-max-w-7xl tw-mx-auto tw-text-center tw-mb-8 sm:tw-mb-12">
            <h2 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-6xl tw-font-semibold tw-text-global-2 tw-mb-8 sm:tw-mb-12">
              Ready To Boost Student Placement Rates?
            </h2>
          </div>

          <div className="tw-max-w-4xl tw-mx-auto tw-bg-[linear-gradient(180deg,#ccf2ff_0%,_#aacad4_100%)] tw-rounded-3xl tw-p-6 sm:tw-p-8 lg:tw-p-12 tw-border tw-border-[linear-gradient(136deg,#ffffff_0%,_#999999_100%)]">
            <h3 className="tw-text-2xl sm:tw-text-3xl lg:tw-text-5xl tw-font-semibold tw-text-global-3 tw-text-center tw-mb-6 sm:tw-mb-8">
              Connect With Us for a Short Demo
            </h3>

            <form className="tw-space-y-6" onSubmit={e => e.preventDefault()}>
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
                <div>
                  <label className="tw-block tw-text-sm tw-font-semibold tw-text-global-2 tw-mb-1">Full Name *</label>
                  <EditText
                    placeholder="John Doe "
                    value={formData.fullName}
                    onChange={handleInputChange('fullName')}
                    className="tw-bg-global-8"
                  />
                  {errors.fullName && <p className="tw-text-red-500 tw-text-xs">{errors.fullName}</p>}
                </div>
                <div>
                  <label className="tw-block tw-text-sm tw-font-semibold tw-text-global-2 tw-mb-1">Institution Name *</label>
                  <EditText
                    placeholder="Example Center"
                    value={formData.institutionName}
                    onChange={handleInputChange('institutionName')}
                    className="tw-bg-global-8"
                  />
                  {errors.institutionName && <p className="tw-text-red-500 tw-text-xs">{errors.institutionName}</p>}
                </div>
              </div>

              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
                <div>
                  <label className="tw-block tw-text-sm tw-font-semibold tw-text-global-2 tw-mb-1">Phone Number *</label>
                  <EditText
                    placeholder="(123) 456-7890"
                    value={formData.phoneNumber}
                    onChange={handleInputChange('phoneNumber')}
                    onKeyDown={handleKeyDown}
                    className="tw-bg-global-8"
                  />

                  {errors.phoneNumber && <p className="tw-text-red-500 tw-text-xs">{errors.phoneNumber}</p>}
                </div>
                <div>
                  <label className="tw-block tw-text-sm tw-font-semibold tw-text-global-2 tw-mb-1">Work Email Address *</label>
                  <EditText
                    placeholder="johndoe@example.edu"
                    value={formData.workEmail}
                    onChange={handleInputChange('workEmail')}
                    className="tw-bg-global-8"
                  />
                  {errors.workEmail && <p className="tw-text-red-500 tw-text-xs">{errors.workEmail}</p>}
                </div>
              </div>

              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
                <div>
                  <label className="tw-block tw-text-sm tw-font-semibold tw-text-global-2 tw-mb-2">Student Population *</label>
                  <div className="tw-flex tw-flex-wrap tw-gap-4">
                    {['<1000', '1000-5000', '5000-15000', '15000+'].map(option => (
                      <label key={option} className="tw-flex tw-items-center tw-gap-2">
                        <input
                          type="radio"
                          name="population"
                          value={option}
                          checked={selectedPopulation === option}
                          onChange={() => handlePopulationChange(option)}
                          className="tw-w-4 tw-h-4 tw-border tw-border-global-6 tw-rounded"
                        />
                        <span className="tw-text-sm tw-font-semibold tw-text-global-2">{option}</span>
                      </label>
                    ))}
                  </div>
                  {errors.student_population && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.student_population}</p>}
                </div>
                <div>
                  <label className="tw-block tw-text-sm tw-font-semibold tw-text-global-2 tw-mb-2">Priority *</label>
                  <div className="tw-flex tw-flex-wrap tw-gap-4">
                    {['Placement Tracking', 'Analytics', 'Jobs'].map(priority => (
                      <label key={priority} className="tw-flex tw-items-center tw-gap-2">
                        <input
                          type="radio"
                          name="priority"
                          value={priority}
                          checked={selectedPriorities === priority}
                          onChange={() => handlePriorityChange(priority)}
                          className="tw-w-4 tw-h-4 tw-border tw-border-global-6 tw-rounded"
                        />
                        <span className="tw-text-sm tw-font-semibold tw-text-global-2">{priority}</span>
                      </label>
                    ))}
                  </div>
                  {errors.priority && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.priority}</p>}
                </div>
              </div>

              <div>
                <label className="tw-block tw-text-sm tw-font-semibold tw-text-global-2 tw-mb-1">Message *</label>
                <textarea
                  placeholder="Enter Your Message"
                  value={formData.message}
                  onChange={e => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  className="tw-w-full tw-border tw-border-[#e1e3ea] tw-rounded-lg tw-bg-global-8 tw-text-edittext-1 tw-p-3 tw-min-h-[100px] tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500 tw-focus:border-blue-500"
                />
                {errors.message && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.message}</p>}
              </div>

              <Button
                variant="primary"
                size="large"
                fullWidth
                disabled={isSubmitting}
                className={`tw-flex tw-border-none tw-items-center tw-border-none tw-justify-center tw-gap-2 tw-bg-[#0843FE] tw-text-white tw-p-[13px] tw-rounded ${
                  isSubmitting ? 'tw-opacity-60 tw-cursor-not-allowed' : ''
                }`}
                style={{ border: 'none' }}
                onClick={handleRequestCall}
              >
                {!isSubmitting && <img src="/images/img_vector_white_a700_12x12.svg" alt="" className="tw-w-5 tw-h-5" />}
                {isSubmitting ? 'Requesting...' : 'Request a Call'}
              </Button>
            </form>
          </div>
        </section>
        <div>
          <CareerFooter theme="light" />
        </div>
        {statusMessage.show && (
          <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50">
            <div className="tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-6 tw-max-w-md tw-w-full tw-text-center">
              <h2 className={`tw-text-lg tw-font-semibold ${statusMessage.type === 'success' ? 'tw-text-green-600' : 'tw-text-red-600'}`}>
                {statusMessage.type === 'success' ? 'Success' : 'Error'}
              </h2>
              <p className="tw-mt-2 tw-text-sm tw-text-gray-700">{statusMessage.text}</p>
              <button
                className="tw-mt-4 tw-px-4 tw-py-2 tw-bg-[#0843FE] tw-border-none tw-text-white tw-rounded hover:tw-bg-blue-700"
                onClick={() => setStatusMessage({ show: false, type: '', text: '' })}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </main>{' '}
    </div>
  );
};

export default CareerCentersPage;
